import type { TErrorReport } from '@/lib/errorLog';
import type { TErrorLogSource } from '@/services/errorLog';

const TOKEN_LIMIT = 64;

const THROWN_FALLBACK = 'Unexpected error';

const SERVICE_SUFFIX = /Service$/;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ID_KEY_SUFFIX = 'ID';

type TErrorOrigin = {
  action: string;
  entityKind: string | null;
};

const isString = (value: unknown): value is string => typeof value === 'string';

const isUUID = (value: unknown): value is string => isString(value) && UUID_PATTERN.test(value);

const toSnakeToken = (value: string): string => value
  .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .slice(0, TOKEN_LIMIT)
  .replace(/^_+|_+$/g, '');

const toBounded = (value: string | null | undefined, limit: number): string | null => {
  if (!value) {
    return null;
  }

  return value.slice(0, limit);
};

const toOriginFromWhere = (where: string | undefined): TErrorOrigin | null => {
  const [service, method] = (where ?? '').split('.');

  if (!service || !method) {
    return null;
  }

  const action = toSnakeToken(method);

  if (!action) {
    return null;
  }

  return { action, entityKind: toSnakeToken(service.replace(SERVICE_SUFFIX, '')) || null };
};

const toOriginFromKey = (key: readonly unknown[] | undefined): TErrorOrigin | null => {
  const [domain, operation] = (key ?? []).filter(isString);

  if (!domain || !operation) {
    return null;
  }

  const action = toSnakeToken(operation);

  if (!action) {
    return null;
  }

  return { action, entityKind: toSnakeToken(domain) || null };
};

const toErrorOrigin = (
  where: string | undefined,
  key: readonly unknown[] | undefined,
  fallbackAction: string,
): TErrorOrigin => toOriginFromWhere(where) ?? toOriginFromKey(key) ?? { action: fallbackAction, entityKind: null };

const toEntityID = (variables: unknown): string | null => {
  if (isUUID(variables)) {
    return variables;
  }

  if (!variables || typeof variables !== 'object' || Array.isArray(variables)) {
    return null;
  }

  const match = Object.entries(variables).find(([key, value]) => key.endsWith(ID_KEY_SUFFIX) && isUUID(value));

  return isUUID(match?.[1]) ? match[1] : null;
};

const toThrownReport = (source: TErrorLogSource, action: string, thrown: unknown): TErrorReport => {
  if (thrown instanceof Error) {
    return {
      source, action, message: `${thrown.name}: ${thrown.message}`, stack: thrown.stack ?? null,
    };
  }

  if (isString(thrown) && thrown.length > 0) {
    return { source, action, message: thrown };
  }

  return { source, action, message: THROWN_FALLBACK };
};

export type {
  TErrorOrigin,
};
export {
  toBounded,
  toEntityID,
  toErrorOrigin,
  toThrownReport,
};
